import { Location } from 'react-router-dom';

type RestorablePathInput = {
  /** The user now holding the session. */
  currentUserId?: string;
  /** Where the user was heading when sign-in interrupted them. */
  from?: Location | string | null;
  /** The user who was signed out, when sign-in was reached via logout. */
  previousUserId?: string;
};

// `from` arrives as a router Location (PrivateRoute) or a bare pathname (useLogout, no-match
// route); anything that could leave the origin is dropped — a leading `//` or `/\` is
// scheme-relative to the browser, not an app path.
const normalizePath = (from: Location | string): string | undefined => {
  const path =
    typeof from === 'string'
      ? from
      : `${from.pathname ?? ''}${from.search ?? ''}${from.hash ?? ''}`;

  if (!path.startsWith('/')) return undefined;
  if (path.startsWith('//') || path.startsWith('/\\')) return undefined;

  return path;
};

/**
 * The interrupted destination to restore after sign-in, or `undefined` when the role's default
 * landing page should win. Restoring is skipped when the session belongs to a different account
 * than the one that was signed out.
 */
export const getRestorablePath = ({
  from,
  currentUserId,
  previousUserId,
}: RestorablePathInput): string | undefined => {
  if (!from) return undefined;
  if (previousUserId && previousUserId !== currentUserId) return undefined;

  return normalizePath(from);
};
